<template>
  <div class="detalle-oferta-panel">
    <DxTabPanel ref="tabPanel" height="95%" tabs-position="top" icon-position="left" :defer-rendering="true">
      <DxItem :key="1" :title="tituloTab1" class="tab-item" :disabled="false" icon="fa-regular fa-file-lines">
        <oferta-energia-tab-datos :model="model" :tiene-tramites-asociados="tieneTramitesAsociados" ref="tabDatos"></oferta-energia-tab-datos>
      </DxItem>
      <DxItem :key="2" :title="tituloTab2" class="tab-item" :disabled="false" icon="fa-regular fa-file-signature">
        <oferta-energia-tab-plantilla :model="model" :tiene-tramites-asociados="tieneTramitesAsociados" ref="tabAnexo"></oferta-energia-tab-plantilla>
      </DxItem>
      <DxItem :key="3" :title="tituloTab3" class="tab-item" :disabled="false" icon="fa-regular fa-percent">
        <oferta-energia-tab-descuentos :model="model" :tiene-tramites-asociados="tieneTramitesAsociados" ref="tabDescuentos"></oferta-energia-tab-descuentos>
      </DxItem>
      <DxItem :key="4" :title="tituloTab4" class="tab-item" :disabled="false" icon="fa-regular fa-euro-sign">
        <oferta-energia-tab-comisiones-panel :model="model" :is-edit-mode="isEditMode" ref="tabComisiones"></oferta-energia-tab-comisiones-panel>
      </DxItem>
    </DxTabPanel>
  </div>
</template>

<script setup lang="ts">
import OfertaEnergiaTabDatos from '@/components/ofertas-energia/base/OfertaEnergiaTabDatos.vue';
import OfertaEnergiaTabPlantilla from '@/components/ofertas-energia/base/OfertaEnergiaTabPlantilla.vue';
import OfertaEnergiaTabDescuentos from '@/components/ofertas-energia/base/OfertaEnergiaTabDescuentos.vue';
import OfertaEnergiaTabComisionesPanel from '@/components/ofertas-energia/base/OfertaEnergiaTabComisionesPanel.vue';
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { type CreateComisionEnergiaOfertaModel, type CreateOfertaEnergiaModel, type DetailOfertaEnergiaModel, type EditOfertaEnergiaModel } from '@/.shared/.api';
import DxTabPanel, { DxItem } from 'devextreme-vue/tab-panel';
import { useIntl } from '@/.shared/services/i18n/i18nservice';
import { useModalBuilder, type ToolbarReference } from '@/.shared/helpers/modal-building-helper';
import type { DxButton } from 'devextreme-vue';
import { messageBoxResult } from '@/.shared/components/dialogs/message-box';
import { toastError, toastSuccess } from '@/.shared/services/notification-service';
import validationEngine from 'devextreme/ui/validation_engine';
import { useApi } from '@/.shared/.api/integration';
import { toUTCDateOnly } from '@/.shared/helpers/date-parser-helper';
import { ApiError } from '@/.shared/services/http';
import { OFFER_WITH_BAD_END_DATE } from '@/.shared/errors/codes';

const intl = useIntl();
const api = useApi();
const tabDatos = ref<InstanceType<typeof OfertaEnergiaTabDatos>>();
const tabAnexo = ref<InstanceType<typeof OfertaEnergiaTabPlantilla>>();
const tabDescuentos = ref<InstanceType<typeof OfertaEnergiaTabDescuentos>>();
const tabComisiones = ref<InstanceType<typeof OfertaEnergiaTabComisionesPanel>>();
const tabPanel = ref<InstanceType<typeof DxTabPanel>>();

const props = defineProps({
  model: {
    type: Object as () => DetailOfertaEnergiaModel | CreateOfertaEnergiaModel,
    required: true
  },
  isEditMode: {
    type: Boolean,
    required: true
  },
  tieneTramitesAsociados: {
    type: Boolean,
    required: true
  }
});

const tituloTab1 = computed(() => `${intl.i18n.t('ofertas.form.datos')}`);
const tituloTab2 = computed(() => `${intl.i18n.t('ofertas.form.plantilla_anexo')}`);
const tituloTab3 = computed(() => `${intl.i18n.t('ofertas.form.descuentos')}`);
const tituloTab4 = computed(() => `${intl.i18n.t('ofertas.form.comisiones')}`);

onMounted(async () => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!)

  configureButtons(modal);
});

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  let okButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;
  ({ okButton, cancelButton } = modal.standardButtons.addOkCancel());
  okButton.update(w => w.text = `${intl.i18n.t("shared.ui.buttons.save")}`);
  okButton.update(w => w.onClick = async () => {
    saveOrEdit().then((value) => {
      if (value)
        modal.close(messageBoxResult.ok)
    });
  }
  );
  cancelButton.update(w => w.onClick = () => modal.close(messageBoxResult.cancel));
}

const hayDatosLuz = computed(() => {
  return props.model && props.model.DatosLuz != null;
})

const saveOrEdit = async () => {
  // Forzar el renderizado de las pestañas necesarias antes de validar
  if (tabPanel.value) {
    const instance = tabPanel.value.instance;
    const currentTabIndex = instance.option('selectedIndex');
    // Activar las pestañas que necesitan validación
    instance.option('selectedIndex', 1);
    instance.option('selectedIndex', 2);
    // Restaurar la pestaña original
    instance.option('selectedIndex', currentTabIndex);
  }

  var res1 = validationEngine.validateGroup("validacionOfertaEnergia");
  var res2 = validationEngine.validateGroup("validacionTipoPersona");
  var res3 = validationEngine.validateGroup("validacionFechaFin");
  var res4 = await tabAnexo.value?.validate();
  var res6 = hayDatosLuz.value ? validationEngine.validateGroup("validacionPotenciaMax") : { isValid: true };
  var res7 = await tabDescuentos.value?.validate();

  if (res1.isValid && res2.isValid && res3.isValid && res4 && res6.isValid && res7) {
    props.model.FechaInicio = toUTCDateOnly(props.model.FechaInicio);
    props.model.FechaFin = toUTCDateOnly(props.model.FechaFin);

    if (props.isEditMode) {
      if (documentoASubir.value)
        await api.callStandardApi("putAnexoOfertaEnergia", {showLoading: false}, (props.model as EditOfertaEnergiaModel).Id, documentoASubir.value?.name, documentoASubir.value)
          .catch(() => {
            toastError(intl.i18n.t('oferta_energia.edicion_anexo_fallo'));
            return false;
          });

        try {
          await api.callStandardApi("editOfertaEnergia", {showLoading: true}, props.model as EditOfertaEnergiaModel)
            .then(() => toastSuccess(intl.i18n.t('oferta_energia.edicion_exito')));
        } catch (e: any) {
          if (e instanceof ApiError && e.details?.errorList.length === 1 && [OFFER_WITH_BAD_END_DATE].includes(e.details.errorList[0].Code)) {
            toastError(intl.i18n.t(`shared.errors.api.${e.details.errorList[0].Code}`))
          }
          else {
            toastError(intl.i18n.t('oferta_energia.edicion_fallo'));
          }
          return false;
        }
    } else {
      if (documentoASubir.value) {
        await api.callStandardApi("postDocumentoTemp", {showLoading: false}, documentoASubir.value?.name, documentoASubir.value)
          .then(async (tempUrl: string) => {
            await api.callStandardApi("createOfertaEnergia", {showLoading: true}, documentoASubir.value?.name, tempUrl, props.model as CreateOfertaEnergiaModel)
              .then((idOferta: string) => {
                crearComisiones(idOferta);
                toastSuccess(intl.i18n.t('oferta_energia.creacion_exito'))
              })
              .catch(() => {
                toastError(intl.i18n.t('oferta_energia.creacion_fallo'));
                return false;
              });
          })
          .catch(() => {
            toastError(intl.i18n.t('oferta_energia.creacion_fallo'));
            return false;
          });
      } else {
        await api.callStandardApi("createOfertaEnergia", {showLoading: true}, undefined, undefined, props.model as CreateOfertaEnergiaModel)
          .then((idOferta: string) => {
            crearComisiones(idOferta);
            toastSuccess(intl.i18n.t('oferta_energia.creacion_exito'))
          })
          .catch(() => {
            toastError(intl.i18n.t('oferta_energia.creacion_fallo'));
            return false;
          });
      }
    }
    return true;
  } else {
    toastError(intl.i18n.t('oferta_energia.revise_validacion'));
  }

  return false;
}

const crearComisiones = async (idOferta: string) => {
  const comisiones = tabComisiones.value?.getComisiones();
  if (comisiones && comisiones.length > 0) {
    // Agrupar comisiones por idMarco
    const comisionesPorMarco = comisiones.reduce((acc, comision) => {
      const idMarco = comision.idMarco;
      if (!acc[idMarco]) {
        acc[idMarco] = [];
      }
      acc[idMarco].push(comision);
      return acc;
    }, {} as Record<string, typeof comisiones>);

    // Crear comisiones para cada marco
    for (const [idMarco, comisionesMarco] of Object.entries(comisionesPorMarco)) {
      await api.callStandardApi("createComisionesEnergiaOferta", {showLoading: false}, idOferta, idMarco, comisionesMarco as CreateComisionEnergiaOfertaModel[]);
    }
  }
};

const documentoASubir = computed(() => {
  return tabAnexo.value?.documentoASubir;
});

</script>